// src/utils/formatters.js

/**
 * Format a date string to a more readable format
 * @param {string} dateString - ISO date string
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, options = {}) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) return 'Invalid Date';

    // Default options for date formatting
    const defaultOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    };

    // Merge default options with provided options
    const formatterOptions = { ...defaultOptions, ...options };

    return new Intl.DateTimeFormat('en-US', formatterOptions).format(date);
};

/**
 * Format a number as currency
 * @param {number} value - The number to format
 * @param {string} currency - Currency code (USD, EUR, etc.)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, currency = 'USD') => {
    if (value === null || value === undefined) return 'N/A';

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
    }).format(value);
};

/**
 * Format a number as a percentage
 * @param {number} value - The decimal value to format (0.5 = 50%)
 * @param {number} digits - Number of decimal places
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, digits = 1) => {
    if (value === null || value === undefined) return 'N/A';

    return new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
    }).format(value);
};

/**
 * Convert camelCase or snake_case to Title Case with spaces
 * @param {string} str - The string to format
 * @returns {string} Formatted string
 */
export const formatLabel = (str) => {
    if (!str) return '';

    // Replace underscores and camelCase with spaces
    const spacedStr = str
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .trim();

    // Capitalize first letter of each word
    return spacedStr
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

/**
 * Truncate text with ellipsis if it exceeds max length
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum allowed length
 * @returns {string} Truncated text
 */
export const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;

    return text.slice(0, maxLength) + '...';
};

export default {
    formatDate,
    formatCurrency,
    formatPercentage,
    formatLabel,
    truncateText
};